import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  absenceService,
  AbsenceRequest,
  AbsenceRequestStatus,
  AbsenceType,
} from '../../services/absence.service'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Badge } from '../../components/ui/badge'
import { CheckCircle, XCircle, Loader2, Calendar, X, CalendarDays } from 'lucide-react'
import AbsenceCalendar from './absence-calendar'
import { PageLayout } from '../../components/layout'
import { Container } from '../../components/ui/container'
import { useLocale } from '../../hooks/use-locale'

type TabType = 'requests' | 'overview' | 'calendar'

export default function AbsenceRequestsPage() {
  const { t } = useTranslation()
  const { formatDate } = useLocale()
  const [requests, setRequests] = useState<AbsenceRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<TabType>('requests')
  const [selectedRequest, setSelectedRequest] = useState<AbsenceRequest | null>(null)
  const [reviewModalOpen, setReviewModalOpen] = useState(false)
  const [reviewComment, setReviewComment] = useState('')
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject' | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | AbsenceRequestStatus>('all')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    loadRequests()
  }, [])

  const loadRequests = async () => {
    try {
      setLoading(true)
      const data = await absenceService.getAllRequests()
      data.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      setRequests(data)
    } catch (error) {
      console.error('Failed to load absence requests:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleReviewClick = (request: AbsenceRequest, action: 'approve' | 'reject') => {
    setSelectedRequest(request)
    setReviewAction(action)
    setReviewComment('')
    setReviewModalOpen(true)
  }

  const handleReviewSubmit = async () => {
    if (!selectedRequest) return

    try {
      setSubmitting(true)
      const status =
        reviewAction === 'approve' ? AbsenceRequestStatus.APPROVED : AbsenceRequestStatus.REJECTED

      await absenceService.updateRequestStatus(
        selectedRequest.id,
        status,
        reviewComment || undefined
      )

      setReviewModalOpen(false)
      setSelectedRequest(null)
      setReviewComment('')
      setReviewAction(null)
      await loadRequests()
    } catch (error) {
      console.error('Failed to update request:', error)
      alert(t('errors.updateAbsenceRequest'))
    } finally {
      setSubmitting(false)
    }
  }

  const getStatusBadge = (status: AbsenceRequestStatus) => {
    switch (status) {
      case AbsenceRequestStatus.APPROVED:
        return <Badge title={t('absence.status.approved')} variant='success' />
      case AbsenceRequestStatus.REJECTED:
        return <Badge title={t('absence.status.rejected')} variant='error' />
      case AbsenceRequestStatus.PENDING:
        return <Badge title={t('absence.status.pending')} variant='warning' />
    }
  }

  const getTypeText = (type: AbsenceType) => {
    switch (type) {
      case AbsenceType.VACATION:
        return t('absence.types.vacation')
      case AbsenceType.SICK_LEAVE:
        return t('absence.types.sickLeave')
      case AbsenceType.UNPAID_LEAVE:
        return t('absence.types.unpaidLeave')
      case AbsenceType.OTHER:
        return t('absence.types.other')
    }
  }

  const formatDateShort = (dateStr: string) =>
    formatDate(dateStr, { day: '2-digit', month: '2-digit', year: 'numeric' })

  const formatDateFull = (dateStr: string) =>
    formatDate(dateStr, {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })

  const filteredRequests = useMemo(() => {
    return requests.filter(r => {
      const matchesStatus = statusFilter === 'all' || r.status === statusFilter
      const matchesSearch =
        searchQuery === '' ||
        `${r.userFirstName} ${r.userLastName}`.toLowerCase().includes(searchQuery.toLowerCase())
      return matchesStatus && matchesSearch
    })
  }, [requests, statusFilter, searchQuery])

  const pendingRequests = filteredRequests.filter(r => r.status === AbsenceRequestStatus.PENDING)
  const reviewedRequests = filteredRequests.filter(r => r.status !== AbsenceRequestStatus.PENDING)

  const absenceOverview = useMemo(() => {
    const overview: Record<string, Array<{ name: string; type: AbsenceType; userId: string }>> = {}

    requests
      .filter(r => r.status === AbsenceRequestStatus.APPROVED)
      .forEach(request => {
        const start = new Date(request.startDate)
        const end = new Date(request.endDate)

        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const dateKey = d.toISOString().split('T')[0]
          if (!overview[dateKey]) {
            overview[dateKey] = []
          }
          overview[dateKey].push({
            name: `${request.userFirstName} ${request.userLastName}`,
            type: request.type,
            userId: request.userId,
          })
        }
      })

    return overview
  }, [requests])

  return (
    <PageLayout isLoading={loading} loadingMessage={t('absence.loading')}>
      <Container>
        <div className='flex items-center justify-between'>
          <div>
            <h1 className='text-xl font-semibold text-neutral-900'>{t('absence.title')}</h1>
            <p className='text-sm text-neutral-500 mt-0.5'>{t('absence.subtitle')}</p>
          </div>
        </div>

        <div className='flex gap-4 mb-6 border-b'>
          <button
            onClick={() => setActiveTab('requests')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'requests'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className='flex items-center gap-2'>
              <CheckCircle className='w-4 h-4' />
              {t('absence.tabManage')}
            </div>
          </button>
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'overview'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className='flex items-center gap-2'>
              <Calendar className='w-4 h-4' />
              {t('absence.tabOverview')}
            </div>
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'calendar'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className='flex items-center gap-2'>
              <CalendarDays className='w-4 h-4' />
              {t('absence.tabCalendar')}
            </div>
          </button>
        </div>

        {activeTab === 'requests' && (
          <>
            <div className='mb-6 bg-gray-50 p-4 rounded-lg'>
              <div className='flex flex-col md:flex-row gap-4'>
                <div className='flex-1'>
                  <input
                    type='text'
                    placeholder={t('absence.searchPlaceholder')}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                  />
                </div>
                <div className='flex gap-2'>
                  <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value as 'all' | AbsenceRequestStatus)}
                    className='px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                  >
                    <option value='all'>{t('absence.filterAll')}</option>
                    <option value={AbsenceRequestStatus.PENDING}>{t('absence.status.pending')}</option>
                    <option value={AbsenceRequestStatus.APPROVED}>{t('absence.status.approved')}</option>
                    <option value={AbsenceRequestStatus.REJECTED}>{t('absence.status.rejected')}</option>
                  </select>
                  {(searchQuery || statusFilter !== 'all') && (
                    <button
                      onClick={() => {
                        setSearchQuery('')
                        setStatusFilter('all')
                      }}
                      className='px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors'
                    >
                      <X className='w-4 h-4' />
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className='mb-8'>
              <h2 className='text-2xl font-semibold mb-4'>
                {t('absence.pendingRequestsTitle', { count: pendingRequests.length })}
              </h2>
              {pendingRequests.length === 0 ? (
                <Card>
                  <CardContent className='py-8'>
                    <p className='text-center text-gray-500'>
                      {searchQuery || statusFilter !== 'all'
                        ? t('absence.noPendingFound')
                        : t('absence.noPending')}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className='grid gap-4'>
                  {pendingRequests.map(request => (
                    <Card
                      key={request.id}
                      className='hover:shadow-lg transition-shadow border-l-4 border-l-yellow-500'
                    >
                      <CardHeader>
                        <div className='flex items-start justify-between gap-4'>
                          <div>
                            <CardTitle className='text-xl'>
                              {request.userFirstName} {request.userLastName}
                            </CardTitle>
                            <div className='flex items-center gap-2 mt-2'>
                              <Badge title={getTypeText(request.type)} variant='primary' />
                              {getStatusBadge(request.status)}
                            </div>
                          </div>
                          <div className='flex gap-2 flex-shrink-0'>
                            <Button
                              onClick={() => handleReviewClick(request, 'approve')}
                              variant='success'
                              className='whitespace-nowrap'
                            >
                              <CheckCircle className='w-4 h-4 mr-1' />
                              {t('absence.approveRequestTitle', {
                                defaultValue: 'Genehmigen',
                              })}
                            </Button>
                            <Button
                              onClick={() => handleReviewClick(request, 'reject')}
                              variant='destructive'
                              className='whitespace-nowrap'
                            >
                              <XCircle className='w-4 h-4 mr-1' />
                              {t('absence.rejectRequestTitle', {
                                defaultValue: 'Ablehnen',
                              })}
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className='grid grid-cols-2 gap-4 mb-4'>
                          <div>
                            <p className='text-sm text-gray-600'>{t('timeTracking.from')}</p>
                            <p className='font-semibold'>{formatDateShort(request.startDate)}</p>
                          </div>
                          <div>
                            <p className='text-sm text-gray-600'>{t('timeTracking.to')}</p>
                            <p className='font-semibold'>{formatDateShort(request.endDate)}</p>
                          </div>
                        </div>
                        {request.reason && (
                          <div className='mt-4 pt-4 border-t'>
                            <p className='text-sm text-gray-600 mb-1'>
                              {t('absence.reasonLabel', { defaultValue: 'Grund:' })}
                            </p>
                            <p className='text-gray-900'>{request.reason}</p>
                          </div>
                        )}
                        <div className='mt-2 text-xs text-gray-500'>
                          {t('absence.requestedAt', {
                            defaultValue: 'Beantragt am {{date}}',
                            date: formatDateFull(request.createdAt.toString()),
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h2 className='text-2xl font-semibold mb-4'>
                {t('absence.reviewedRequestsTitle', { count: reviewedRequests.length })}
              </h2>
              {reviewedRequests.length === 0 ? (
                <Card>
                  <CardContent className='py-8'>
                    <p className='text-center text-gray-500'>
                      {searchQuery || statusFilter !== 'all'
                        ? t('absence.noReviewedFound')
                        : t('absence.noReviewed')}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className='grid gap-4'>
                  {reviewedRequests.map(request => (
                    <Card
                      key={request.id}
                      className={`opacity-75 border-l-4 ${
                        request.status === AbsenceRequestStatus.APPROVED
                          ? 'border-l-green-500'
                          : 'border-l-red-500'
                      }`}
                    >
                      <CardHeader>
                        <div className='flex items-start justify-between'>
                          <div>
                            <CardTitle className='text-lg'>
                              {request.userFirstName} {request.userLastName}
                            </CardTitle>
                            <div className='flex items-center gap-2 mt-2'>
                              <Badge title={getTypeText(request.type)} variant='primary' />
                              {getStatusBadge(request.status)}
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className='grid grid-cols-2 gap-4 mb-4'>
                          <div>
                            <p className='text-sm text-gray-600'>{t('timeTracking.from')}</p>
                            <p className='font-semibold'>{formatDateShort(request.startDate)}</p>
                          </div>
                          <div>
                            <p className='text-sm text-gray-600'>{t('timeTracking.to')}</p>
                            <p className='font-semibold'>{formatDateShort(request.endDate)}</p>
                          </div>
                        </div>
                        {request.reviewComment && (
                          <div className='mt-4 pt-4 border-t'>
                            <p className='text-sm text-gray-600 mb-1'>
                              {t('absence.commentLabel', { defaultValue: 'Anmerkung:' })}
                            </p>
                            <p className='text-gray-900 italic'>{request.reviewComment}</p>
                          </div>
                        )}
                        {request.reviewedAt && (
                          <div className='mt-2 text-xs text-gray-500'>
                            {request.status === AbsenceRequestStatus.APPROVED
                              ? t('absence.status.approved')
                              : t('absence.status.rejected')}{' '}
                            {t('absence.reviewedAt', {
                              defaultValue: 'am {{date}}',
                              date: formatDateFull(request.reviewedAt.toString()),
                            })}
                            {request.reviewerFirstName &&
                              ` ${t('absence.reviewedBy', {
                                defaultValue: 'von {{name}}',
                                name: `${request.reviewerFirstName} ${request.reviewerLastName}`,
                              })}`}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'overview' && (
          <div>
            <h2 className='text-2xl font-semibold mb-4'>{t('absence.overviewTitle')}</h2>
            <div className='grid gap-4'>
              {Object.entries(absenceOverview)
                .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
                .map(([date, absences]) => (
                  <Card key={date} className='border-l-4 border-l-blue-500'>
                    <CardHeader>
                      <CardTitle className='text-lg'>
                        {formatDateFull(date + 'T00:00:00')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className='space-y-2'>
                        {absences.map((absence, idx) => (
                          <div
                            key={`${absence.userId}-${idx}`}
                            className='flex items-center justify-between p-3 bg-gray-50 rounded-lg'
                          >
                            <div>
                              <p className='font-semibold text-gray-900'>{absence.name}</p>
                              <p className='text-sm text-gray-600'>{getTypeText(absence.type)}</p>
                            </div>
                            <Badge
                              title={getTypeText(absence.type)}
                              variant={
                                absence.type === AbsenceType.VACATION
                                  ? 'primary'
                                  : absence.type === AbsenceType.SICK_LEAVE
                                    ? 'error'
                                    : 'secondary'
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              {Object.keys(absenceOverview).length === 0 && (
                <Card>
                  <CardContent className='py-8'>
                    <p className='text-center text-gray-500'>{t('absence.noApprovedAbsences')}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {activeTab === 'calendar' && <AbsenceCalendar requests={requests} />}

        {reviewModalOpen && selectedRequest && (
          <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
            <Card className='w-full max-w-md'>
              <CardHeader>
                <CardTitle>
                  {reviewAction === 'approve'
                    ? t('absence.approveRequestTitle')
                    : t('absence.rejectRequestTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div>
                  <p className='text-sm font-semibold text-gray-700'>
                    {selectedRequest.userFirstName} {selectedRequest.userLastName}
                  </p>
                  <p className='text-sm text-gray-600 mt-1'>
                    {formatDateShort(selectedRequest.startDate)} {t('timeTracking.to').toLowerCase()}{' '}
                    {formatDateShort(selectedRequest.endDate)}
                  </p>
                </div>
                <textarea
                  placeholder={t('absence.commentPlaceholder')}
                  value={reviewComment}
                  onChange={e => setReviewComment(e.target.value)}
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none'
                  rows={4}
                />
                <div className='flex gap-3 pt-4'>
                  <Button
                    onClick={() => {
                      setReviewModalOpen(false)
                      setSelectedRequest(null)
                    }}
                    disabled={submitting}
                    className='flex-1'
                    variant='secondary'
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={handleReviewSubmit}
                    disabled={submitting}
                    variant={reviewAction === 'approve' ? 'success' : 'destructive'}
                    className='flex-1'
                  >
                    {submitting ? (
                      <>
                        <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                        {t('common.processing')}
                      </>
                    ) : reviewAction === 'approve' ? (
                      <>
                        <CheckCircle className='w-4 h-4 mr-2' />
                        {t('absence.approveRequestTitle', { defaultValue: 'Genehmigen' })}
                      </>
                    ) : (
                      <>
                        <XCircle className='w-4 h-4 mr-2' />
                        {t('absence.rejectRequestTitle', { defaultValue: 'Ablehnen' })}
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </Container>
    </PageLayout>
  )
}
